/**
 * AuthContext - Authentication State Management
 * Provides user authentication state and methods (login, logout).
 * After login, verifies the user is a member of the "admins" team.
 * Only admins team members can access the dashboard.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { account, teamsClient, ADMINS_TEAM_ID } from '../appwrite';

// Create the authentication context
const AuthContext = createContext(null);

/**
 * Custom hook to access the auth context.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider component — wraps the app and provides:
 * - user: current authenticated user (null if not logged in or not an admin)
 * - loading: whether the initial auth check is in progress
 * - login(email, password): authenticates and verifies admin access
 * - logout(): ends the current session
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Checks if there is an existing authenticated session
   * AND if the user is a member of the admins team.
   */
  const checkSession = async () => {
    try {
      const currentUser = await account.get();
      // Verify user is in the admins team
      const isAdmin = await verifyAdminAccess();
      if (isAdmin) {
        setUser(currentUser);
      } else {
        // User is logged in but NOT an admin — clear session
        await account.deleteSession({ sessionId: 'current' });
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verifies the current user is a member of the admins team.
   * Uses the client SDK (which checks the logged-in user's memberships).
   * @returns {boolean} true if user is in the admins team
   */
  const verifyAdminAccess = async () => {
    try {
      // This will succeed if the logged-in user is a member of the admins team
      await teamsClient.get({ teamId: ADMINS_TEAM_ID });
      return true;
    } catch (error) {
      // 404 or 403 = user is not a member of this team
      return false;
    }
  };

  /**
   * Logs in with email/password and verifies the user is an admin.
   */
  const login = async (email, password) => {
    await account.createEmailPasswordSession({ email, password });
    const currentUser = await account.get();

    // Verify admin team membership
    const isAdmin = await verifyAdminAccess();
    if (!isAdmin) {
      // Not an admin — destroy the session and throw
      await account.deleteSession({ sessionId: 'current' });
      throw new Error('Access denied. You are not a member of the admins team.');
    }

    setUser(currentUser);
    return currentUser;
  };

  /**
   * Logs out the current user.
   */
  const logout = async () => {
    await account.deleteSession({ sessionId: 'current' });
    setUser(null);
  };

  const value = { user, loading, login, logout };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
